/**
 * Bitácora institucional (append-only, sólo consulta).
 *
 * Fuente de verdad: backend (`/api/bitacora`). El registro (`logBitacora`)
 * es fire-and-forget async: no bloquea a los callers que ya lo invocan
 * en flujos síncronos. La lectura hidrata desde backend a demanda.
 */
import { api } from "@/services/api";

const KEY = "ct_admin_bitacora_v1";
const KEY_SESSION = "ct_admin_session_v1";

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

// Guarda local liviana: si hay snapshot de sesión, asumimos que la
// cookie HttpOnly también existe. El backend rechaza con 401 si no.
// Se evita import cruzado con adminAuthStore para prevenir ciclos.
const haySesionAdmin = () => {
  if (!isBrowser()) return false;
  try {
    return !!window.localStorage.getItem(KEY_SESSION);
  } catch {
    return false;
  }
};

const read = () => {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const write = (arr) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
};

export const ACCIONES = {
  LOGIN: "LOGIN",
  LOGOUT: "LOGOUT",
  CREAR_EVENTO: "CREAR_EVENTO",
  EDITAR_EVENTO: "EDITAR_EVENTO",
  PUBLICAR: "PUBLICAR",
  CANCELAR: "CANCELAR",
  CERRAR: "CERRAR",
  DUPLICAR: "DUPLICAR",
  PAUSAR: "PAUSAR_VENTA",
  REANUDAR: "REANUDAR_VENTA",
  CREAR_USUARIO: "CREAR_USUARIO",
  EDITAR_USUARIO: "EDITAR_USUARIO",
  ACTIVAR_USUARIO: "ACTIVAR_USUARIO",
  DESACTIVAR_USUARIO: "DESACTIVAR_USUARIO",
  CONFIGURACION: "CONFIGURACION",
  NOTIFICACION: "NOTIFICACION",
};

export const MODULOS = {
  AUTH: "Autenticación",
  EVENTOS: "Eventos",
  USUARIOS: "Usuarios",
  NOTIFICACIONES: "Notificaciones",
  CONFIGURACION: "Configuración",
};

/**
 * Registra una entrada en la bitácora. Fire-and-forget async.
 * - Requiere token admin (si no hay, no persiste en backend).
 * - No lanza jamás. No bloquea al caller.
 */
export const logBitacora = ({ accion, modulo, detalle }) => {
  if (!haySesionAdmin()) return;
  api
    .post("/bitacora", {
      accion: accion || "-",
      modulo: modulo || "-",
      detalle: detalle || "",
    })
    .catch(() => {
      /* silencioso: no romper flujo por errores de red. */
    });
};

/** Hidrata el cache local desde el backend. */
export const hidratarBitacora = async () => {
  if (!haySesionAdmin()) return [];
  try {
    const { data } = await api.get("/bitacora");
    if (Array.isArray(data)) {
      write(data);
      return data;
    }
  } catch {
    /* ignore */
  }
  return read();
};

/** Lectura síncrona desde cache. */
export const listarBitacora = () =>
  read().slice().sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
